import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { showToastNotification } from "../../../App/state/actions";
import recordingConstants from "../../../shared/constants/recordingConstants";
import constants from "../../../shared/constants";
import { trackRecordingError, trackGenericAction } from "../../../shared/analytics/dataLayer";
import { useReducers } from "../../../shared/hooks/useReducer";
import { ANALYTICS_EVENT_TYPES, MAPPED_CONTENT_TYPES } from "../../../shared/constants/analytics";

const { REDUCER_TYPE } = constants;
const { RECORDING_PARAMS } = recordingConstants;

const useRecordingToast = (isMR) => {
  const setRecordingResponse = useSelector(({ recording }) => recording.setRecordingResponse);
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const { featureToggles } = useReducers(REDUCER_TYPE.APP);
  const { isDynatraceTrackingEnabled } = featureToggles;
  const recordingEventTypeRef = useRef(null);

  const getRecordingToastMessage = useCallback(
    (recordingInfo, actionType, recordingType) => {
      let toastMsg;
      if (recordingInfo.code) {
        const triggerTrackRecordingErrorEvent = (toastMsg) => {
          trackRecordingError(
            recordingEventTypeRef.current,
            toastMsg,
            {
              errorCode: setRecordingResponse?.code,
              errorMessage: setRecordingResponse?.message,
            },
            isDynatraceTrackingEnabled
          );
          recordingEventTypeRef.current = null;
        };
        if (actionType === "set") {
          toastMsg = translate(RECORDING_PARAMS.RECORDING_NOT_SET);
          triggerTrackRecordingErrorEvent(translate(RECORDING_PARAMS.RECORDING_NOT_SET));
        } else if (actionType === "edit") {
          toastMsg = translate(
            RECORDING_PARAMS[
              recordingType === RECORDING_PARAMS.SERIES ? "SERIES_RECORDING_NOT_UPDATED" : "RECORDING_NOT_UPDATED"
            ]
          );
        } else if (actionType === "delete") {
          toastMsg = translate(RECORDING_PARAMS.COULD_NOT_CANCEL_RECORDING);
          triggerTrackRecordingErrorEvent(translate(RECORDING_PARAMS.COULD_NOT_CANCEL_RECORDING));
        }
      } else {
        if (actionType === "set") {
          let msg = "RECORDING_SET";
          if (recordingType === RECORDING_PARAMS.SERIES) {
            msg = "SERIES_RECORDING_SET";
          } else if (recordingType === RECORDING_PARAMS.TEAM) {
            msg = "TEAM_RECORDING_SET";
          }
          toastMsg = translate(RECORDING_PARAMS[msg]);
        } else if (actionType === "edit") {
          toastMsg = translate(
            RECORDING_PARAMS[
              recordingType === RECORDING_PARAMS.SERIES ? "SERIES_RECORDING_UPDATED" : "RECORDING_UPDATED"
            ]
          );
        } else if (actionType === "delete") {
          let msg = "RECORDING_CANCELLED";
          if (recordingType === RECORDING_PARAMS.SERIES) {
            msg = "SERIES_RECORDING_CANCELLED";
          } else if (recordingType.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase()) {
            msg = "TEAM_RECORDING_CANCELLED";
          }
          toastMsg = translate(RECORDING_PARAMS[msg]);
        }
      }

      return toastMsg;
    },
    [translate, setRecordingResponse, isDynatraceTrackingEnabled]
  );

  const displayRecordingToast = useCallback(
    (recordingInfo, manipulatedRecordingParams) => {
      if (recordingInfo && manipulatedRecordingParams) {
        const toastMsg = getRecordingToastMessage(
          recordingInfo,
          manipulatedRecordingParams.actionType,
          manipulatedRecordingParams.recordingType
        );
        if (toastMsg) {
          dispatch(showToastNotification(toastMsg));
          // Fire recording start analytics for successful "set" actions that came through the modal path.
          // The direct path (no modal) fires analytics inline and nulls the ref immediately,
          // so this block only triggers for modal-initiated recordings (e.g. sport events with a seriesId).
          if (
            recordingEventTypeRef.current === ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START &&
            manipulatedRecordingParams.actionType === "set" &&
            !recordingInfo.code
          ) {
            const isSeriesEvent = manipulatedRecordingParams.recordingType === RECORDING_PARAMS.SERIES;
            const isTeamRecording =
              manipulatedRecordingParams.recordingType?.toLowerCase() === RECORDING_PARAMS.TEAM.toLowerCase();
            const teamRecordingPayload = {};
            if (isTeamRecording) {
              const sports = recordingInfo?.assetToRecord?.metadata?.extendedMetadata?.dlum?.sports;
              // Match the teamUmId to determine which team (home/guest) was selected for recording
              const teamUmId = manipulatedRecordingParams.targetAssetInfo?.filterBy?.teamUmId;
              teamRecordingPayload.teamSport =
                recordingInfo?.assetToRecord?.metadata?.extendedMetadata?.seriesTitle || sports?.name;
              if (teamUmId === sports?.homeUmId) {
                teamRecordingPayload.teamName = sports?.homeTeam;
              } else if (teamUmId === sports?.guestUmId) {
                teamRecordingPayload.teamName = sports?.guestTeam;
              }
            }
            trackGenericAction(
              ANALYTICS_EVENT_TYPES.VIDEO_RECORD_START,
              {
                isItemLive: true,
                isSeriesEvent,
                isSeriesRecording: isSeriesEvent,
                isTeamRecording,
                ...teamRecordingPayload,
                mappedContentType: isMR ? MAPPED_CONTENT_TYPES.LPVR : MAPPED_CONTENT_TYPES.CPVR,
                ...recordingInfo?.assetToRecord,
              },
              isDynatraceTrackingEnabled
            );
          }
          recordingEventTypeRef.current = null;
        }
      }
    },
    [getRecordingToastMessage, dispatch, isDynatraceTrackingEnabled, isMR]
  );

  return { displayRecordingToast, recordingEventTypeRef };
};

export default useRecordingToast;
